import { Stack, Typography } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { TAddress, getAddressLines, getAddressString } from '@verifime/utils';
import tokens from '@verifime/design-tokens';

/**
 * `'commaSplit'` (default) is today's behaviour: `getAddressString`'s single formatted
 * string, split on its commas, one `Typography` per fragment. `'semantic'` instead
 * renders `getAddressLines`'s two structured lines directly (A14): a street line and a
 * locality line, with no trailing country line.
 *
 * A string-literal discriminant rather than a boolean, because `strictNullChecks` is
 * `false` in this repo and boolean unions do not narrow correctly under that setting
 * (see `PermissionBlock`'s docblock for the same reasoning).
 *
 * `'commaSplit'` stays the default deliberately: nine call sites across client-portal,
 * customer-portal and admin-portal render this component with no test harness of its
 * own protecting them, so their behaviour must not move. Only the confidence page opts
 * into `'semantic'`. Migrating the other call sites is a follow-up issue.
 */
export type TDisplayAddressFormat = 'commaSplit' | 'semantic';

export default function DisplayAddress({
  address,
  showIcon = true,
  format = 'commaSplit',
}: {
  address: TAddress;
  showIcon?: boolean;
  format?: TDisplayAddressFormat;
}) {
  if (format === 'semantic') {
    const lines = getAddressLines(address).filter(Boolean);

    if (lines.length < 1) {
      return;
    }

    return (
      <Stack direction="row" gap={tokens.spacingXs}>
        {showIcon && <LocationOnIcon />}
        <Stack direction="column" gap={tokens.spacingXs}>
          {lines.map((line, index) => (
            <Typography key={index} variant="body2">
              {line}
            </Typography>
          ))}
        </Stack>
      </Stack>
    );
  }

  const multiLineAddress = (getAddressString(address)?.split(',') || []).filter((part) =>
    Boolean(part.replace(/^\s+$/g, '')),
  );

  if (multiLineAddress.length < 1) {
    return;
  }

  return (
    <Stack direction="row" gap={tokens.spacingXs}>
      {showIcon && <LocationOnIcon />}
      <Stack direction="column" gap={tokens.spacingXs}>
        {multiLineAddress.map((part, index) => (
          <Typography key={index} variant="body2">
            {part}
            {index !== multiLineAddress.length - 1 && ','}
          </Typography>
        ))}
      </Stack>
    </Stack>
  );
}
